import axios from 'axios';
import { sessionManager } from '../auth/sessionManager';
import {
  API_BASE_URL,
  SSE_BASE_URL,
  VITE_AUTH_ACCESS_TOKEN,
  VITE_AUTH_USER_NAME,
} from '../environment';
import { API } from './api';
import { client } from './client';
import { useTimezoneStore } from '../store/timezoneStore';
import { toUtcOffsetTimestamp } from '../utils/timezoneUtils';

// Function to fetch token from external API
// Uses fetch with credentials: 'omit' to ensure no cookies are sent with this request
// NOTE: This function will return null if credentials are not configured (for production environments)
export const fetchAccessToken = async () => {
  // Check if credentials are configured - skip API call if not set (local testing only)
  if (!VITE_AUTH_USER_NAME || !VITE_AUTH_ACCESS_TOKEN) {
    return null;
  }

  const params = new URLSearchParams({
    grant_type: 'password',
    username: 'credentials',
    password: VITE_AUTH_USER_NAME,
  });

  const response = await fetch(
    `${API_BASE_URL}${API.AUTH_ENDPOINT}?${params.toString()}`,
    {
      method: 'POST',
      headers: {
        UserAuthorization: 'partner',
        Authorization: `Basic ${VITE_AUTH_ACCESS_TOKEN}`,
        'Content-Type': 'application/json',
      },
      // credentials: 'omit' ensures no cookies are sent with this request
      credentials: 'omit',
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch access token: ${response.status}`);
  }

  return response.json();
};

export const postMultiDevicesInformation = async payload => {
  const response = await client.post(API.MULTI_DEVICES_INFORMATION, payload, {
    skipLoader: true,
  });
  return response.data;
};

export const postReportMissingConst = async payload => {
  const response = await client.post(API.REPORT_MISSING_CONST, payload, {
    skipLoader: true,
  });
  return response.data;
};

export const saveMultiDeviceData = async payload => {
  const response = await client.post(API.MULTI_DEVICES_SAVE_DATA, payload, {
    skipLoader: true,
  });
  return response.data;
};

/**
 * GET /information fans out to a slow upstream on the server side, so it can take tens of
 * seconds. Two independent callers mount at the same time and each used to start its own
 * request: the device info panel and the monitoring version rows. The two requests then
 * competed and the later one resolved roughly twice as late as the first, which is what delayed
 * the software update row from appearing.
 *
 * A caller arriving while a request for the same device is still in flight joins that request
 * instead of starting another. The entry is dropped as soon as it settles, so a deliberate
 * re-read (after sending an update command, say) still hits the server.
 */
const deviceInformationInFlight = new Map();

export const getDeviceInformation = deviceId => {
  const pending = deviceInformationInFlight.get(deviceId);
  if (pending) return pending;

  const request = client
    .get(API.DEVICES_INFORMATION(deviceId), {
      skipLoader: true,
    })
    .finally(() => deviceInformationInFlight.delete(deviceId));

  deviceInformationInFlight.set(deviceId, request);
  return request;
};

/**
 * Connection state ({ di, state: ONLINE | OFFLINE | UNKNOWN, lastUpdateDate }) from STREAM_API.
 * It lives on the SSE host, so it goes through fetch with the session token like the SSE calls.
 */
export const getDeviceConnectionState = async deviceId => {
  const response = await fetch(
    `${SSE_BASE_URL}/devices/${encodeURIComponent(deviceId)}/health`,
    { headers: { Authorization: `Bearer ${sessionManager.getToken()}` } }
  );
  if (!response.ok) {
    throw new Error(`Device health request failed: ${response.status}`);
  }
  return response.json();
};

export const getSmartThingsData = deviceId =>
  client.get(API.SMARTTHINGS_DATA(deviceId), {
    skipLoader: true,
  });

export const getDeviceTimezone = async deviceId => {
  const response = await client.get(API.DEVICE_TIMEZONE(deviceId), {
    skipLoader: true,
    skipErrorNotification: true,
  });
  return response.data;
};

export const postServiceInformation = async payload => {
  const response = await client.post(
    API.SERVICE_INFORMATION(payload.svcId),
    payload,
    {
      skipLoader: true,
    }
  );
  return response.data;
};

export const updateServiceInformation = async payload => {
  const requestPayload = Object.prototype.hasOwnProperty.call(payload, 'updatedTime')
    ? {
        ...payload,
        updatedTime: toUtcOffsetTimestamp(
          payload.updatedTime,
          useTimezoneStore.getState().getActiveTimezone()
        ),
      }
    : payload;
  const response = await client.post(
    API.SERVICE_INFORMATION_UPDATE(payload.svcId),
    requestPayload,
    // This field has already been converted to UTC in the legacy format expected
    // by the service-information API; the common interceptor must not reformat it.
    { preserveUtcDateTimeFields: ['updatedTime'] }
  );
  return response.data;
};

export const getDeviceTypeUsingPinCodeAndUuid = async (
  uuid,
  deviceType,
  pincode,
  svcId
) => {
  const response = await client.get(
    API.GET_DEVICE_TYPE_USING_PINCODE_AND_UUID(uuid, deviceType, pincode),
    {
      params: { svcId },
    }
  );
  return response.data;
};

export const getDeviceTypeUisngPinCode = async (
  deviceType,
  pincode,
  svcId,
  uuid
) => {
  if (uuid) {
    return getDeviceTypeUsingPinCodeAndUuid(uuid, deviceType, pincode, svcId);
  }
  const response = await client.get(API.GET_DEVICE_TYPE(deviceType, pincode), {
    params: { svcId },
  });
  return response.data;
};

export const getDeviceErrorHistory = devceId =>
  client.get(API.DEVICE_ERROR_HISTORY(devceId), {
    skipLoader: true,
  });

export const getDeviceOperationHistory = devceId =>
  client.get(API.DEVICE_OPERATION_HISTORY(devceId), {
    skipLoader: true,
  });

export const getServiceInformationEndtype = params =>
  client.get(API.SERVICE_INFORMATION_ENDTYPE, { params: params });

export const saveData = async payload => {
  const response = await client.post(API.SAVE_DATA, payload, {
    skipLoader: true,
  });
  return response.data;
};

export const postSVCResult = payload => {
  const svcId = payload.serviceInfo.svcId;
  return client.post(API.SAVE_RESULT_APIENDPOINT(svcId), payload);
};

export const postUpdateSvcEzoneHistory = async payload => {
  const response = await client.post(API.UPDATE_SVC_HISTORY, payload);
  return response.data;
};

export const getServiceInformation = svcId => {
  return client.get(API.SERVICE_INFORMATION(svcId), {
    skipLoader: true,
  });
};

export const postDevicermstate = ({ uuid, saUid, payload }) => {
  const headers = {
    'X-User-Id': saUid,
    'X-Request-Id': 'random',
  };

  if (typeof sessionStorage !== 'undefined' && payload?.rmMode === 'true') {
    sessionStorage.setItem('rmModeEnabled', 'true');
  }

  return client.post(API.POST_DEVICE_RM_STATE(uuid), payload, { headers });
};

/** Ends a remote-control session started with rmMode 'true'; no-op when none is active. */
export const releaseDevicermstate = ({ uuid, saUid }) => {
  if (
    typeof sessionStorage === 'undefined' ||
    sessionStorage.getItem('rmModeEnabled') !== 'true'
  ) {
    return null;
  }
  sessionStorage.removeItem('rmModeEnabled');
  return postDevicermstate({ uuid, saUid, payload: { rmMode: 'false' } });
};

export const postDevicermstateForced = (uuid, deviceType, saUid) => {
  const headers = {
    'X-User-Id': saUid,
    'X-Request-Id': 'random',
    'Content-Type': 'application/json',
  };
  return client.post(
    API.POST_DEVICE_RM_STATE_PINCODE_FORCED(uuid, deviceType),
    null,
    { headers }
  );
};

//device apiservice

export const getDeviceAnalyticSymptom = (deviceUUID, queryParams) => {
  return client.get(API.DEVICE_ANALYTICS_SYMPTOM(deviceUUID), {
    params: queryParams,
    skipLoader: true,
  });
};

export const getDeviceErrorSymptom = (deviceUUID, queryParams) => {
  return client.get(API.DEVICE_ERROR_SYMPTOM(deviceUUID), {
    params: queryParams,
    skipLoader: true,
  });
};

export const getDeviceAnalyticSummary = deviceUUID => {
  return client.get(API.DEVICE_ANALYTICS_SUMMARY(deviceUUID), {
    skipLoader: true,
    skipErrorNotification: true,
  });
};

export const deviceAnalysticsFeedback = payload => {
  const deviceUUID = payload['di'];
  return client.post(API.DEVICE_ANALYTICS_FEEDBACK(deviceUUID), payload, {
    skipLoader: true,
    preserveUtcDateTimeFields: ['request_time'],
  });
};

// Errors stay inside the feedback popover, so the global error toast is skipped.
export const postAgentFeedback = payload =>
  client.post(API.AGENT_FEEDBACK, payload, {
    skipLoader: true,
    skipErrorNotification: true,
  });

export const postScreenshotStart = payload => {
  const headers = {
    'X-User-Id': payload?.saUid,
    'X-Request-Id': 'random',
  };

  return client.post(API.SCREENSHOT_START, payload.data, { headers });
};

export const getDeviceStates = deviceUUID => {
  return client.get(API.DEVICE_STATES(deviceUUID), {
    skipLoader: true,
  });
};
export const getDevicesUisngPinCode = (deviceType, pincode, svcId) => {
  return client.get(API.GET_DEVICE_LIST_USING_PINCODE(deviceType, pincode), {
    params: { svcId },
  });
};
export const updateMode = (deviceUUID, payload, saUid) => {
  const headers = {
    'X-User-Id': saUid,
    'X-Request-Id': 'random',
  };
  return client.post(API.DEVICE_MODE(deviceUUID), payload, {
    headers,
  });
};

// FHub API
export const updateFhub = (deviceId, payload, saUid) => {
  const headers = {
    'X-User-Id': saUid,
    'X-Request-Id': 'random',
  };
  return client.post(API.UPDATE_FHUB(deviceId), payload, { headers });
};

export const getScreenShareImage = async (url, key) => {
  const res = await axios.get(url, {
    headers: {
      'x-api-key': key,
    },
  });
  return res;
};
export const postRCDiagnosis = (deviceId, payload) => {
  return client.post(API.DEVICE_RC_DIAGNOSIS(deviceId), payload);
};
// Device map history
export const getDeviceMapHistory = (deviceId, queryParams) => {
  return client.get(API.DEVICE_MAP_HISTORY(deviceId), {
    params: queryParams,
    skipLoader: true,
  });
};

//washer state upadte
const getHeader = saUid => {
  return {
    'Content-Type': 'application/json',
    'X-User-Id': saUid,
    'X-Request-Id': 'rendom',
  };
};
export const handleRemoteControl = (di, payload, saUid) => {
  const headers = getHeader(saUid);
  return client.post(API.DEVICE_CONTROL_ENDPOINT(di), payload, { headers });
};

const modeKeys = [
  'mode.modes',
  'options.Blooming',
  'options.Volume',
  'options.OnTimer',
  'options.OffTimer',
  'options.Autoclean',
  'options.Display',
  'options.Booster',
  'options.ComfortAICooling',
  'options.DiagnosisAI',
  'options.SmartCoolClean',
];

const modeConvenientKeys = ['comode.comode'];
const modeOptionsKeys = ['options.Sleep', 'options.Light', 'options.Panel'];

export const updateAirconditionerState = (
  param,
  payload,
  saUid,
  di,
  unitId
) => {
  const headers = getHeader(saUid);

  // Spec changed: Always send unitId as 0 for subdevice control
  let updatedPayload = {
    ...payload,
    unitId: 0,
    ...([...modeKeys, ...modeConvenientKeys, ...modeOptionsKeys].includes(param)
      ? {
          convenient: modeConvenientKeys.includes(param) ? true : false,
          modeType: modeOptionsKeys.includes(param) ? 'options' : 'mode',
        }
      : {}),
  };
  console.log(updatedPayload);
  if (param.startsWith('airconditioner') && payload.options) {
    return client.post(API.DEVICE_CONTROL_ENDPOINT(di), updatedPayload, {
      headers,
    });
  }

  return client.post(API.DEVICE_CONTROL_ENDPOINT(di), updatedPayload, {
    headers,
  });
};

/**
 * Save data using fetch with keepalive (for beforeunload)
 * Guarantees delivery even after page closes
 */
export const saveDataWithKeepalive = payload => {
  const token = sessionManager.getToken();
  return fetch(`${API_BASE_URL}${API.SAVE_DATA}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
    keepalive: true,
  });
};

/**
 * Post SVC result using fetch with keepalive (for beforeunload)
 * Guarantees delivery even after page closes
 */
export const postSVCResultWithKeepalive = payload => {
  const token = sessionManager.getToken();
  const svcId = payload.serviceInfo.svcId;
  return fetch(`${API_BASE_URL}${API.SAVE_RESULT_APIENDPOINT(svcId)}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
    keepalive: true,
  });
};

/**
 * Post device RM state using fetch with keepalive (for beforeunload)
 * Guarantees delivery even after page closes
 * Includes custom headers: X-User-Id, X-Request-Id
 */
export const postDevicermstateWithKeepalive = ({ uuid, saUid, payload }) => {
  const token = sessionManager.getToken();
  return fetch(`${API_BASE_URL}${API.POST_DEVICE_RM_STATE(uuid)}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'X-User-Id': saUid,
      'X-Request-Id': 'random',
    },
    body: JSON.stringify(payload),
    keepalive: true,
  });
};

/**
 * Post SmartThings link (noise/image) to device
 * @param {string} deviceUuid - Device UUID
 * @param {string} type - Link type: "noise" or "image"
 * @param {string} saUid - Service agent user ID
 * @returns {Promise} Axios promise
 */
export const postSmartThingsLink = (deviceUuid, type, saUid) => {
  const headers = {
    'X-User-Id': saUid,
    'X-Request-Id': 'random',
  };

  const payload = {
    type: type,
    device_id: deviceUuid,
  };

  return client.post(API.SMARTTHINGS_LINK, payload, { headers });
};
